import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAuth } from '../../../../../../components/AuthContext/context.ts';
import {
  GetDataPartnerOptionsDocument,
  type GetDataPartnerOptionsQuery,
  type GetDataPartnerOptionsQueryVariables,
} from '../../../../../../graphql/query/audiences/GetDataPartnerOptions.generated.tsx';
import {
  GetProvidersDocument,
  type GetProvidersQuery,
} from '../../../../../../graphql/query/audiences/GetProviders.generated.tsx';
import { isAdmin } from '../../../../../../permissions/validators.ts';
import { AudienceFields, type AudienceForm } from '../../../types.ts';
import { buildDataPartnerFilter, type MarketContext } from './utils/buildDataPartnerFilter.ts';
import { mapArrayToIdName } from './utils/mapArrayToIdName.ts';

export type ProviderMap = Record<string, string>;
export type OrganizationMap = Record<string, string>;

export type UseReferenceDataResponse = {
  loading: boolean;
  providers: ProviderMap;
  organizations: OrganizationMap;
};

type UseReferenceDataParams = {
  marketContext?: MarketContext;
};

export const useReferenceData = ({ marketContext }: UseReferenceDataParams = {}): UseReferenceDataResponse => {
  const { control } = useFormContext<AudienceForm>();
  const { session } = useAuth();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control });

  const accountIdVariables = isAdmin(session) ? { accountId: observableAccountId } : {};
  const accountUid = isAdmin(session) ? observableAccountId : undefined;
  const dataPartnerFilter = buildDataPartnerFilter({ accountUid, marketContext });
  const dataPartnerVariables = dataPartnerFilter ? { filter: dataPartnerFilter } : {};

  const { data: organizationsList, loading: organizationsLoading } = useQuery<
    GetDataPartnerOptionsQuery,
    GetDataPartnerOptionsQueryVariables
  >(GetDataPartnerOptionsDocument, {
    variables: dataPartnerVariables,
  });

  const { data: providersList, loading: providersLoading } = useQuery<GetProvidersQuery>(GetProvidersDocument, {
    variables: { ...accountIdVariables },
  });

  const providersMap: ProviderMap = useMemo(() => {
    return providersList?.provider ? mapArrayToIdName(providersList?.provider) : {};
  }, [providersList]);

  const organizationsMap: OrganizationMap = useMemo(() => {
    const options = (organizationsList?.optionsByPath ?? []).filter(
      (option): option is NonNullable<GetDataPartnerOptionsQuery['optionsByPath'][number]> => Boolean(option)
    );

    return mapArrayToIdName(options);
  }, [organizationsList?.optionsByPath]);

  return {
    loading: organizationsLoading || providersLoading,
    providers: providersMap,
    organizations: organizationsMap,
  };
};
